import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProductCreateRequest,
  ProductListQuery,
  ProductListResponse,
  ProductResponse,
  ProductUpdateRequest,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<ProductListQuery>) => ['products', 'list', q] as const,
  one: (id: string) => ['products', 'one', id] as const,
};

function toQueryString(q: Partial<ProductListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.search) params.set('search', q.search);
  if (q.category) params.set('category', q.category);
  if (q.status) params.set('status', q.status);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useProducts(query: Partial<ProductListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<ProductListResponse>(`/api/products${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['products', 'one', 'NIL'],
    queryFn: () => api<ProductResponse>(`/api/products/${id}`),
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductCreateRequest) =>
      api<ProductResponse>('/api/products', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductUpdateRequest) =>
      api<ProductResponse>(`/api/products/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/products/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
}
